import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { DateRange } from "react-date-range";
import type { DateRangeProps, Range, RangeKeyDict } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useDateRangeThemeSx } from "../hooks/useDateRangeThemeSx";
import { useLocaleFormat } from "../i18n/useLocaleFormat";
import { useTranslation } from "react-i18next";
import {
  clampYmdRangeToBounds,
  formatYmd,
  parseYmdToLocalDate,
} from "../miContenido/dates";

/** Workaround: @types/react-date-range class typings vs React 18 JSX (TS2786). */
const DateRangeCalendar = DateRange as React.ComponentType<DateRangeProps>;

type PresetId =
  | "today"
  | "yesterday"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth"
  | "thisYear"
  | "lastYear"
  | "allTime"
  | "custom";

const PRESET_ITEMS: { id: Exclude<PresetId, "custom">; label: string }[] = [
  { id: "today", label: "Hoy" },
  { id: "yesterday", label: "Ayer" },
  { id: "thisWeek", label: "Esta semana" },
  { id: "lastWeek", label: "Semana pasada" },
  { id: "thisMonth", label: "Este mes" },
  { id: "lastMonth", label: "Mes pasado" },
  { id: "thisYear", label: "Este año" },
  { id: "lastYear", label: "Año pasado" },
  { id: "allTime", label: "Todo el período" },
];

const PRESET_SIDEBAR_WIDTH = 180;

function isFullYmd(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && value >= "1900-01-01";
}

export function formatRangeLabel(
  startDate: string,
  endDate: string,
  numberLocale = "es-CL",
): string {
  const fmt = (ymd: string) => {
    const date = parseYmdToLocalDate(ymd);
    return date.toLocaleDateString(numberLocale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };
  return `${fmt(startDate)} – ${fmt(endDate)}`;
}

function rangeFromDraft(draft: Range[]): { start: string; end: string } | null {
  const sel = draft[0];
  if (!sel?.startDate || !sel?.endDate) return null;
  let start = formatYmd(sel.startDate);
  let end = formatYmd(sel.endDate);
  if (start > end) [start, end] = [end, start];
  return { start, end };
}

function buildPresetRange(
  id: Exclude<PresetId, "custom">,
  today: Date,
  minBound: string | undefined,
  maxBound: string | undefined,
): { startDate: string; endDate: string } {
  let start: Date;
  let end: Date;
  switch (id) {
    case "today":
      start = today;
      end = today;
      break;
    case "yesterday": {
      const y = subDays(today, 1);
      start = y;
      end = y;
      break;
    }
    case "thisWeek":
      start = startOfWeek(today, { weekStartsOn: 1 });
      end = endOfWeek(today, { weekStartsOn: 1 });
      break;
    case "lastWeek": {
      const ref = subWeeks(today, 1);
      start = startOfWeek(ref, { weekStartsOn: 1 });
      end = endOfWeek(ref, { weekStartsOn: 1 });
      break;
    }
    case "thisMonth":
      start = startOfMonth(today);
      end = endOfMonth(today);
      break;
    case "lastMonth": {
      const ref = subMonths(today, 1);
      start = startOfMonth(ref);
      end = endOfMonth(ref);
      break;
    }
    case "thisYear":
      start = startOfYear(today);
      end = endOfYear(today);
      break;
    case "lastYear": {
      const ref = new Date(today);
      ref.setFullYear(today.getFullYear() - 1);
      start = startOfYear(ref);
      end = endOfYear(ref);
      break;
    }
    case "allTime":
      start = minBound ? parseYmdToLocalDate(minBound) : startOfYear(today);
      end = maxBound ? parseYmdToLocalDate(maxBound) : today;
      break;
  }
  let startYmd = formatYmd(start);
  let endYmd = formatYmd(end);
  if (endYmd > formatYmd(today)) endYmd = formatYmd(today);
  if (minBound && maxBound) {
    return clampYmdRangeToBounds(startYmd, endYmd, minBound, maxBound);
  }
  if (minBound && startYmd < minBound) startYmd = minBound;
  if (maxBound && endYmd > maxBound) endYmd = maxBound;
  if (startYmd > endYmd) return { startDate: endYmd, endDate: endYmd };
  return { startDate: startYmd, endDate: endYmd };
}

function matchPresetId(
  start: string,
  end: string,
  today: Date,
  minBound: string | undefined,
  maxBound: string | undefined,
): PresetId {
  for (const item of PRESET_ITEMS) {
    const r = buildPresetRange(item.id, today, minBound, maxBound);
    if (r.startDate === start && r.endDate === end) return item.id;
  }
  return "custom";
}

function toSelectionRange(start: string, end: string): Range[] {
  return [
    {
      startDate: parseYmdToLocalDate(start),
      endDate: parseYmdToLocalDate(end),
      key: "selection",
    },
  ];
}

function PresetRow({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "block",
        width: "100%",
        boxSizing: "border-box",
        textAlign: "left",
        border: 0,
        cursor: "pointer",
        font: "inherit",
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        color: "text.primary",
        bgcolor: selected ? "action.selected" : "transparent",
        "&:hover": {
          bgcolor: "action.hover",
        },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={selected ? 600 : 400}
        color="inherit"
        noWrap
      >
        {label}
      </Typography>
    </Box>
  );
}

export interface DayRangePickerProps {
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  minDate?: string;
  maxDate?: string;
  /** Field size; defaults to small to match Explorer Select / TextField filters. */
  size?: "small" | "medium";
  /** "below": under the field, right edges aligned (default). "right": beside the field, top edges aligned. */
  popoverPlacement?: "below" | "right";
  /** Shows the range and does not open the editor. */
  disabled?: boolean;
}

const DayRangePicker: React.FC<DayRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  minDate,
  maxDate,
  size = "small",
  popoverPlacement = "below",
  disabled = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const { dateFnsLocale, numberLocale } = useLocaleFormat();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
  const dateRangeThemeSx = useDateRangeThemeSx();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const today = useMemo(() => {
    if (maxDate) return parseYmdToLocalDate(maxDate);
    return new Date();
  }, [maxDate]);

  const [rangeDraft, setRangeDraft] = useState<Range[]>(() =>
    toSelectionRange(startDate, endDate),
  );
  const [activePreset, setActivePreset] = useState<PresetId>(() =>
    matchPresetId(startDate, endDate, today, minDate, maxDate),
  );

  const label = useMemo(
    () => formatRangeLabel(startDate, endDate, numberLocale),
    [startDate, endDate, numberLocale],
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const [labelWidth, setLabelWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    let cancelled = false;
    const measure = () => {
      const input = inputRef.current;
      const ctx = document.createElement("canvas").getContext("2d");
      if (cancelled || !input || !ctx) return;
      ctx.font = window.getComputedStyle(input).font;
      setLabelWidth(Math.ceil(ctx.measureText(label).width) + 2);
    };
    measure();
    document.fonts?.ready.then(measure);
    return () => {
      cancelled = true;
    };
  }, [label]);

  const draftYmd = useMemo(() => rangeFromDraft(rangeDraft), [rangeDraft]);

  const pickerMinDate = useMemo(
    () => (minDate ? parseYmdToLocalDate(minDate) : undefined),
    [minDate],
  );
  const pickerMaxDate = useMemo(
    () => (maxDate ? parseYmdToLocalDate(maxDate) : undefined),
    [maxDate],
  );

  const openPicker = (e: React.MouseEvent<HTMLElement>) => {
    setRangeDraft(toSelectionRange(startDate, endDate));
    setActivePreset(matchPresetId(startDate, endDate, today, minDate, maxDate));
    setAnchorEl(e.currentTarget);
  };

  const closePicker = () => setAnchorEl(null);

  const setDraftFromYmd = (start: string, end: string, preset: PresetId) => {
    let s = start;
    let e = end;
    if (s > e) [s, e] = [e, s];
    if (minDate && maxDate) {
      const clamped = clampYmdRangeToBounds(s, e, minDate, maxDate);
      s = clamped.startDate;
      e = clamped.endDate;
    }
    setRangeDraft(toSelectionRange(s, e));
    setActivePreset(preset);
  };

  const applyPreset = (id: PresetId) => {
    if (id === "custom") {
      setActivePreset("custom");
      return;
    }
    const r = buildPresetRange(id, today, minDate, maxDate);
    setDraftFromYmd(r.startDate, r.endDate, id);
  };

  const applyRange = () => {
    if (!draftYmd) return;
    let { start, end } = draftYmd;
    if (minDate && maxDate) {
      const clamped = clampYmdRangeToBounds(start, end, minDate, maxDate);
      start = clamped.startDate;
      end = clamped.endDate;
    }
    onChange(start, end);
    closePicker();
  };

  const [startInput, setStartInput] = useState(draftYmd?.start ?? "");
  const [endInput, setEndInput] = useState(draftYmd?.end ?? "");

  useEffect(() => {
    setStartInput(draftYmd?.start ?? "");
    setEndInput(draftYmd?.end ?? "");
  }, [draftYmd?.start, draftYmd?.end]);

  const isWithinBounds = (ymd: string) =>
    (!minDate || ymd >= minDate) && (!maxDate || ymd <= maxDate);

  const nextManualRange = (which: "start" | "end", value: string) =>
    which === "start"
      ? { start: value, end: draftYmd?.end ?? value }
      : { start: draftYmd?.start ?? value, end: value };

  // Native date inputs emit intermediate values while the year is typed
  // (e.g. "0002-10-14"), so only commit live when the value is already in
  // bounds and ordered; anything else is clamped on blur.
  const onManualDateChange = (which: "start" | "end", value: string) => {
    (which === "start" ? setStartInput : setEndInput)(value);
    if (!isFullYmd(value) || !isWithinBounds(value)) return;
    const next = nextManualRange(which, value);
    if (next.start > next.end) return;
    setDraftFromYmd(next.start, next.end, "custom");
  };

  const onManualDateBlur = (which: "start" | "end") => {
    const value = which === "start" ? startInput : endInput;
    if (!isFullYmd(value)) {
      setStartInput(draftYmd?.start ?? "");
      setEndInput(draftYmd?.end ?? "");
      return;
    }
    const next = nextManualRange(which, value);
    if (next.start === draftYmd?.start && next.end === draftYmd?.end) return;
    setDraftFromYmd(next.start, next.end, "custom");
  };

  return (
    <>
      <TextField
        size={size}
        value={label}
        disabled={disabled}
        onClick={disabled ? undefined : openPicker}
        inputRef={inputRef}
        inputProps={{
          readOnly: true,
          "aria-label": "Rango de fechas",
          size: Math.max(label.length + 1, 18),
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CalendarMonthOutlinedIcon
                sx={{ fontSize: size === "small" ? 18 : 20 }}
                aria-hidden
              />
            </InputAdornment>
          ),
        }}
        sx={{
          width: "auto",
          maxWidth: "100%",
          cursor: disabled ? "default" : "pointer",
          "& .MuiInputBase-root": {
            cursor: disabled ? "default" : "pointer",
            height: size === "small" ? 40 : undefined,
            boxSizing: "border-box",
            width: "auto",
          },
          "& .MuiInputBase-input": {
            cursor: disabled ? "default" : "pointer",
            whiteSpace: "nowrap",
            textOverflow: "clip",
            width: labelWidth != null ? `${labelWidth}px` : "auto",
          },
        }}
      />
      <Popover
        open={!disabled && Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePicker}
        anchorOrigin={
          popoverPlacement === "right"
            ? { vertical: "top", horizontal: "right" }
            : { vertical: "bottom", horizontal: "right" }
        }
        transformOrigin={
          popoverPlacement === "right"
            ? { vertical: "top", horizontal: "left" }
            : { vertical: "top", horizontal: "right" }
        }
        slotProps={{
          paper: {
            sx: {
              p: 0,
              overflow: "hidden",
              maxWidth: "calc(100vw - 16px)",
              bgcolor: "background.paper",
              backgroundImage: "none",
            },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="stretch"
          sx={{ maxHeight: { xs: "80vh", md: "none" }, overflow: "auto" }}
        >
          <Stack
            spacing={0.25}
            sx={(theme) => ({
              flex: `0 0 ${PRESET_SIDEBAR_WIDTH}px`,
              width: PRESET_SIDEBAR_WIDTH,
              minWidth: PRESET_SIDEBAR_WIDTH,
              maxWidth: PRESET_SIDEBAR_WIDTH,
              boxSizing: "border-box",
              px: 0.75,
              py: 1,
              bgcolor: "background.paper",
              borderRight: `1px solid ${theme.palette.divider}`,
            })}
          >
            {PRESET_ITEMS.map((item) => (
              <PresetRow
                key={item.id}
                label={t(`dates.${item.id}`)}
                selected={activePreset === item.id}
                onClick={() => applyPreset(item.id)}
              />
            ))}
          </Stack>

          <Box
            sx={{
              ...dateRangeThemeSx,
              px: 1,
              pt: 1,
              minWidth: 0,
              flex: "1 1 auto",
              overflow: "auto",
            }}
          >
            <DateRangeCalendar
              ranges={rangeDraft}
              shownDate={parseYmdToLocalDate(startDate)}
              onChange={(ranges: RangeKeyDict) => {
                const sel = ranges.selection;
                if (!sel) return;
                setRangeDraft([{ ...sel, key: "selection" }]);
                setActivePreset("custom");
              }}
              minDate={pickerMinDate}
              maxDate={pickerMaxDate}
              moveRangeOnFirstSelection={false}
              months={isNarrow ? 1 : 2}
              direction="horizontal"
              locale={dateFnsLocale}
              weekStartsOn={1}
              showDateDisplay={false}
              // Left month is the range start, right month is the next one, so a
              // cross-month end (26 Oct → 2 Nov) is on screen after the first click.
              calendarFocus="forwards"
              color={theme.palette.primary.main}
              rangeColors={[theme.palette.primary.main]}
            />
          </Box>
        </Stack>

        <Stack
          direction="row"
          alignItems="stretch"
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
          })}
        >
          <Box
            sx={(theme) => ({
              flex: `0 0 ${PRESET_SIDEBAR_WIDTH}px`,
              width: PRESET_SIDEBAR_WIDTH,
              minWidth: PRESET_SIDEBAR_WIDTH,
              maxWidth: PRESET_SIDEBAR_WIDTH,
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              px: 0.75,
              borderRight: `1px solid ${theme.palette.divider}`,
            })}
          >
            <PresetRow
              label="Personalizado"
              selected={activePreset === "custom"}
              onClick={() => applyPreset("custom")}
            />
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            sx={{ flex: "1 1 auto", px: 2, py: 1.25, minWidth: 0 }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <TextField
                size="small"
                type="date"
                value={startInput}
                onChange={(e) => onManualDateChange("start", e.target.value)}
                onBlur={() => onManualDateBlur("start")}
                inputProps={{
                  min: minDate,
                  max: draftYmd?.end ?? maxDate,
                }}
                sx={{ width: { xs: "100%", sm: 148 } }}
                InputLabelProps={{ shrink: true }}
                aria-label="Fecha inicio"
              />
              <Box component="span" color="text.secondary">
                –
              </Box>
              <TextField
                size="small"
                type="date"
                value={endInput}
                onChange={(e) => onManualDateChange("end", e.target.value)}
                onBlur={() => onManualDateBlur("end")}
                inputProps={{
                  min: draftYmd?.start ?? minDate,
                  max: maxDate,
                }}
                sx={{ width: { xs: "100%", sm: 148 } }}
                InputLabelProps={{ shrink: true }}
                aria-label="Fecha fin"
              />
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" variant="outlined" onClick={closePicker}>
                Cancelar
              </Button>
              <Button size="small" variant="contained" onClick={applyRange}>
                Aplicar
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

export default DayRangePicker;
